import { ReactNode } from "react";
import { useQueryClient } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { IPolicy, tInvitationAction, TStyleConnotation } from "../../../types";
import InviteButton from "./inviteButton";
import InvitationService, {
  Invitation,
} from "../../../services/invitation/invitation.service";
import { tInvitationContext } from "../../../types/invitation.type";
import { IOrganisation } from "../../../services/admin/admin.service";

interface Props {
  action: tInvitationAction;
  button?: ReactNode;
  context?: tInvitationContext;
  invitation?: Invitation;
  orgId?: IOrganisation["id"];
  policyId?: IPolicy["id"];
  type: "user" | "client";
  successRoute?: string;
}

const InviteUser = ({
  action,
  button,
  context,
  invitation,
  orgId,
  policyId,
  type,
  successRoute,
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  const cancelInvite = async () => {
    if (invitation) {
      await InvitationService.cancelInvitation(invitation);
      queryClient.refetchQueries(["use-single-policy-query", policyId]);
      queryClient.refetchQueries("organisation-portfolio-client-list");
      queryClient.refetchQueries(
        `portfolio-client-${invitation.policy_number}`,
      );
    } else {
      throw new Error("No invitation to cancel");
    }
  };

  const buttonAction = (): void => {
    let url;

    switch (action) {
      case "cancel":
        cancelInvite();
        break;
      default:
        if (orgId != null) {
          url = `/invitations/broker/invite?id=${orgId}`;
        } else if (policyId != null) {
          url = `/invitations/policy-holder/invite?id=${policyId}`;
        } else {
          url = "/invitations/broker/invite";
        }

        history.push(url, {
          type,
          successRoute: successRoute ?? location,
        });
    }
  };

  const buttonColor = (): TStyleConnotation => {
    switch (action) {
      case "send":
        if (context === "list-header") {
          return "surface";
        }
        return "accent";
      case "resend":
        return "accent";
      default:
        return "surface";
    }
  };

  const buttonText = (): string | undefined => {
    if (!action) return;

    switch (action) {
      case "send":
        return `Invite ${type}`;
      case "resend":
        return "Resend invite";
      case "cancel":
        return "Cancel invite";
    }
  };

  const buttonVariant = (): "text" | "contained" => {
    switch (context) {
      case "list":
      case "overflow":
        return "text";
      default:
        if (action === "cancel") {
          return "text";
        }
        return "contained";
    }
  };

  return (
    <>
      {button ?? (
        <InviteButton
          context={context}
          color={buttonColor()}
          onClick={buttonAction}
          text={buttonText()}
          type={type}
          variant={buttonVariant()}
        />
      )}
    </>
  );
};

InviteUser.defaults = {
  action: "send",
  type: "client",
};

export default InviteUser;
